import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Copy, Link2, UserPlus, X } from "lucide-react";

export default function MembersDialog({ board, members, onMembersChange, currentUser }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/boards/${board.id}/invite`, { email });
      onMembersChange([...members, data]);
      setEmail("");
      toast.success(`Added ${data.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const generateLink = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/boards/${board.id}/invite-link`);
      const url = `${window.location.origin}/invite/${data.token}`;
      setInviteLink(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const remove = async (memberId) => {
    try {
      await api.delete(`/boards/${board.id}/members/${memberId}`);
      onMembersChange(members.filter((m) => m.id !== memberId));
      toast.success("Member removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const canManage = currentUser && board && (board.owner_id === currentUser.id || currentUser.role === "admin");

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setInviteLink("");
          setCopied(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 sm:gap-2 border border-zinc-200 px-2.5 sm:px-3 py-2 text-xs font-medium hover:border-black active:border-black transition-colors"
          data-testid="board-members-button"
          aria-label="Board members"
        >
          <UserPlus className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Members</span>
          <span className="font-mono-tight text-zinc-400">{members.length}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-zinc-200 sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">
            Board members
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-4 max-h-[40vh] overflow-y-auto">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between border border-zinc-200 px-3 py-2.5"
              data-testid={`member-${m.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
                <div className="text-xs text-zinc-500 truncate">{m.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {board.owner_id === m.id ? (
                  <span className="font-mono-tight text-[10px] uppercase tracking-wider text-zinc-500">
                    Owner
                  </span>
                ) : canManage ? (
                  <button
                    onClick={() => remove(m.id)}
                    className="text-zinc-400 hover:text-red-500 active:text-red-500 p-1 -m-1"
                    data-testid={`member-remove-${m.id}`}
                    aria-label="Remove member"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {canManage && (
          <>
            <form onSubmit={invite} className="mt-4 border-t border-zinc-200 pt-4">
              <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Invite member
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="email@company.com"
                  className="flex-1 border border-zinc-200 px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:outline-none focus:border-black"
                  data-testid="member-invite-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2.5 sm:py-2 text-sm bg-black text-white hover:bg-zinc-800 disabled:opacity-50"
                  data-testid="member-invite-submit"
                >
                  Invite
                </button>
              </div>
              <div className="text-xs text-zinc-400 mt-2">User must be registered first.</div>
            </form>

            <div className="mt-4 border-t border-zinc-200 pt-4">
              <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                <Link2 className="h-3 w-3" /> Invite link
              </div>
              {!inviteLink ? (
                <button
                  type="button"
                  onClick={generateLink}
                  disabled={generating}
                  className="inline-flex items-center gap-2 border border-zinc-200 px-3 py-2 text-xs font-medium hover:border-black disabled:opacity-50 transition-colors"
                  data-testid="invite-link-generate"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  {generating ? "Generating..." : "Generate invite link"}
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    value={inviteLink}
                    className="flex-1 border border-zinc-200 px-3 py-2.5 sm:py-2 text-xs font-mono-tight focus:outline-none focus:border-black"
                    onFocus={(e) => e.target.select()}
                    data-testid="invite-link-input"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 text-sm bg-black text-white hover:bg-zinc-800"
                    data-testid="invite-link-copy"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              )}
              <div className="text-xs text-zinc-400 mt-2">
                Anyone with this link can join. Expires in 14 days.
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
